import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { CourseSummary } from '../../types'
import { Alert, Badge, Card, CardBody, EmptyState, Skeleton } from '../../components/ui'
import { CheckIcon, DocIcon } from '../../components/icons'

export default function CourseList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['learning', 'courses'],
    queryFn: () => api.get<{ ok: boolean; courses: CourseSummary[] }>('/learning/courses'),
  })

  const courses = data?.courses ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Training &amp; courses</h1>
          <p className="page-head__sub">
            Complete a course&rsquo;s modules and pass its assessments to earn certification.
          </p>
        </div>
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="grid grid--2">
          <Skeleton h={120} /><Skeleton h={120} /><Skeleton h={120} /><Skeleton h={120} />
        </div>
      ) : courses.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState icon={<DocIcon size={22} />} title="No courses available"
                        body="Training courses will appear here once they're published." />
          </CardBody>
        </Card>
      ) : (
        /* carousel: on the PWA this becomes a horizontal swipe strip with
           the next course peeking in from the right; desktop still renders
           the same 2-column grid it always has (the class is inert there). */
        <div className="grid grid--2 carousel">
          {courses.map((c) => (
            <Link key={c.course_id} to={`/learning/${c.course_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Card hover style={{ height: '100%' }}>
                <div className="stat" style={{ gap: 'var(--sp-3)' }}>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>{c.course_name}</div>
                    {c.is_mandatory && <Badge tone="warning">Mandatory</Badge>}
                  </div>
                  <p className="page-head__sub" style={{ margin: 0 }}>{c.course_description}</p>
                  <div className="row" style={{ gap: 'var(--sp-4)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                    {c.estimated_duration && <span>{c.estimated_duration}</span>}
                    {c.level && <span>{titleCase(c.level)}</span>}
                    <span>{c.module_count} module{c.module_count === 1 ? '' : 's'}</span>
                  </div>
                  {c.completed ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--success)', fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                      <CheckIcon size={15} /> Completed
                    </div>
                  ) : c.started ? (
                    <div className="onboard__bar">
                      <div className="onboard__fill" style={{ width: `${c.progress_pct ?? 0}%` }} />
                    </div>
                  ) : (
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Not started</span>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  )
}
